import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAnimal, useFarms, useLots, updateAnimalLocal, findMotherByLomoLocal } from "@/lib/repo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { IronBrandCanvas } from "@/components/IronBrandCanvas";
import { IronBrandPhoto } from "@/components/IronBrandPhoto";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES_BOVINO, CATEGORIES_BUFALINO } from "@/lib/utils";

export default function EditAnimal() {
  const { id } = useParams();
  const navigate = useNavigate();
  const animal = useAnimal(id);
  const farms = useFarms();
  const [data, setData] = useState(null);
  const lots = useLots(data?.farm_id);
  const [saving, setSaving] = useState(false);
  const [motherHint, setMotherHint] = useState(null);

  useEffect(() => { if (animal && !data) setData(animal); }, [animal, data]);

  const lookupMother = async (lomo) => {
    if (!data) return;
    // Fix R (2026-11-04): el lomo se repite. Se respeta la madre ya vinculada por chip y, si
    // no, solo se vincula cuando hay una sola hembra posible.
    const { mother: m, ambiguous } = await findMotherByLomoLocal(lomo, { farmId: data.farm_id, chip: data.mother_chip, excludeId: id });
    if (m) {
      setData((d) => ({ ...d, mother_id: m.id, mother_chip: m.chip_code || null }));
      setMotherHint({ found: true, label: `Vinculada · Lomo ${m.lomo_number} · ${m.category || "vaca"}` });
    } else {
      setData((d) => ({ ...d, mother_id: null, mother_chip: null }));
      setMotherHint(lomo ? { found: false, label: ambiguous ? "Hay más de una hembra con ese lomo — no se vincula; se guarda el número igual" : "No está registrada — se guarda el número igual" } : null);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateAnimalLocal(id, data);
      toast.success("Cambios guardados");
      navigate(`/animales/${id}`);
    } catch (err) {
      toast.error(err?.userMessage || "No pude guardar");
    } finally { setSaving(false); }
  };

  if (!data) return null;

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={()=>navigate(-1)} className="h-9 px-2"><ArrowLeft size={18} /></Button>
        <h1 className="font-heading text-2xl font-semibold">Editar animal</h1>
      </div>
      <form onSubmit={submit} className="space-y-4 bg-card border border-border rounded-2xl p-5">
        <div>
          <Label>Código de chip RFID</Label>
          <Input value={data.chip_code || ""} disabled className="h-11 font-mono" />
          <p className="text-xs text-muted-foreground mt-1">El chip no se puede modificar (es identidad del animal).</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Lomo</Label><Input value={data.lomo_number || ""} onChange={(e)=>setData({ ...data, lomo_number: e.target.value })} className="h-11" data-testid="edit-animal-lomo" /></div>
          <div><Label>Tatuaje</Label><Input value={data.ear_tattoo || ""} onChange={(e)=>setData({ ...data, ear_tattoo: e.target.value })} className="h-11" /></div>
          <div>
            <Label>Sexo</Label>
            <Select value={data.sex || "female"} onValueChange={(v)=>setData({ ...data, sex: v })}>
              <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="female">Hembra</SelectItem><SelectItem value="male">Macho</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Raza</Label><Input value={data.breed || ""} onChange={(e)=>setData({ ...data, breed: e.target.value })} className="h-11" /></div>
          <div><Label>Fecha nacimiento</Label><Input type="date" value={data.birth_date?.slice(0,10) || ""} onChange={(e)=>setData({ ...data, birth_date: e.target.value })} className="h-11" /></div>
          <div>
            <Label>Categoría</Label>
            <Select value={data.category || ""} onValueChange={(v)=>setData({ ...data, category: v })}>
              <SelectTrigger className="h-11" data-testid="edit-animal-category"><SelectValue placeholder="-" /></SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Bovino</SelectLabel>
                  {CATEGORIES_BOVINO.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>Bufalino</SelectLabel>
                  {CATEGORIES_BUFALINO.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Finca</Label>
            <Select value={data.farm_id} onValueChange={(v)=>setData({ ...data, farm_id: v, lot_id: "" })}>
              <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{farms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Lote</Label>
            <Select value={data.lot_id || "none"} onValueChange={(v)=>setData({ ...data, lot_id: v === "none" ? null : v })}>
              <SelectTrigger className="h-11"><SelectValue placeholder="Sin lote" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Sin lote</SelectItem>{lots.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Estado</Label>
            <Select value={data.status || "active"} onValueChange={(v)=>setData({ ...data, status: v })}>
              <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Activo</SelectItem>
                <SelectItem value="sold">Vendido</SelectItem>
                <SelectItem value="dead">Muerto</SelectItem>
                <SelectItem value="transferred">Trasladado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Lomo de la madre</Label>
            <Input data-testid="edit-animal-mother-lomo" value={data.mother_lomo || ""} onChange={(e)=>setData({ ...data, mother_lomo: e.target.value })} onBlur={()=>lookupMother(data.mother_lomo)} className="h-11" placeholder="Opcional" />
            {motherHint && (
              <p className={`text-xs mt-1 flex items-center gap-1 ${motherHint.found ? "text-[hsl(var(--state-present))]" : "text-muted-foreground"}`} data-testid="edit-animal-mother-hint">
                {motherHint.found && <CheckCircle2 size={12} />}{motherHint.label}
              </p>
            )}
          </div>
        </div>
        <div><Label>Observaciones</Label><Textarea value={data.observations || ""} onChange={(e)=>setData({ ...data, observations: e.target.value })} rows={3} /></div>
        <div>
          <Label>Hierro (pierna / paleta)</Label>
          <Input data-testid="edit-animal-iron-brand" value={data.iron_brand || ""} onChange={(e)=>setData({ ...data, iron_brand: e.target.value })} className="h-11" placeholder="Marca de hierro" />
          <p className="text-xs text-muted-foreground mt-1 mb-2">Podés dibujar el diseño del hierro o subir una foto para identificarlo mejor.</p>
          <IronBrandCanvas value={data.iron_brand_drawing} onChange={(v)=>setData({ ...data, iron_brand_drawing: v })} />
          {/* Fix C (2026-08-24): además del dibujo, foto real del hierro (pedido del cliente). */}
          <div className="mt-2">
            <IronBrandPhoto value={data.iron_brand_photo} onChange={(v)=>setData({ ...data, iron_brand_photo: v })} />
          </div>
        </div>
        <div className="flex gap-2">
          <Button type="submit" disabled={saving} className="h-12 bg-emerald-700 hover:bg-emerald-800 flex-1" data-testid="edit-animal-submit">{saving ? "Guardando…" : "Guardar cambios"}</Button>
          <Button type="button" variant="outline" className="h-12" onClick={()=>navigate(-1)}>Cancelar</Button>
        </div>
      </form>
    </div>
  );
}
